const bwipjs = require("bwip-js");
import loadBusinessLogo from "./loadBusinessLogo";

const COLORS = { accent: "#2563EB", ink: "#0F172A", muted: "#64748B", border: "#CBD5E1", soft: "#F1F5F9" };
const clean = value => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return ["null", "undefined", "n/a"].includes(text.toLowerCase()) ? "" : text;
};
const compact = values => values.map(clean).filter(Boolean);
const cityLine = (city, state, zip) => compact([compact([city, state]).join(", "), zip]).join(" ");
const unitsPerBox = detail => {
  const configured = Number(detail.Product && detail.Product.unitCountInBox);
  if (configured > 0) return configured;
  const match = clean(detail.name).match(/(?:pack|pk)\s*(\d+)\b/i);
  return match ? Number(match[1]) : 1;
};
const contactStack = (lines, label) => ({
  width: "*",
  stack: [
    { text: label, color: COLORS.muted, bold: true, fontSize: 8, margin: [0, 0, 0, 6] },
    ...lines.map((line, index) => ({ text: line, color: COLORS.ink, bold: index === 0, fontSize: index === 0 ? 11 : 9, lineHeight: 1.2 })),
  ],
});
const makeCode = options => {
  const canvas = document.createElement("canvas");
  return new Promise((resolve, reject) => bwipjs(canvas, options, error => {
    if (error) reject(error);
    else resolve(canvas.toDataURL("image/png"));
  }));
};
export default {
  async invoice(invoice, business, settings) {
    const customer = invoice.Customer || {};
    const metadata = business.metadata || {};
    const currency = settings.options.filters.currency;
    const logo = await loadBusinessLogo(business);
    const showInvoiceBarcode = metadata.barcodeOnInvoice === true;
    const barcode = showInvoiceBarcode
      ? await makeCode({ bcid: "code128", text: String(invoice.localId), scale: 3, height: 4, includetext: false })
      : null;
    const paymentQr = metadata.paymentQrCodeEnabled && clean(metadata.paymentLink)
      ? await makeCode({ bcid: "qrcode", text: metadata.paymentLink, scale: 4 }) : null;
    const exitQr = metadata.invoiceExitGuardEnabled && settings.exitQrPayload
      ? await makeCode({ bcid: "qrcode", text: settings.exitQrPayload, scale: 4 }) : null;
    const phone = value => clean(value) ? settings.formatPhoneNumber(value) : "";

    const businessLines = compact([business.name, business.address1, cityLine(business.city, business.state, business.zip), phone(business.phoneNumber), business.email, business.website]);
    const billingLines = compact([customer.name || "Walk-In Customer", customer.address, cityLine(customer.city, customer.state, customer.zip), phone(customer.phoneNumber), customer.email]);
    const shippingLines = compact([customer.name, customer.shippingAddress, cityLine(customer.shippingCity, customer.shippingState, customer.shippingZip)]);
    const showLineNumber = metadata.showLineNumberOnInvoice !== false;
    const showUnitPrice = metadata.showUnitPriceOnInvoice !== false;
    const showBoxes = metadata.boxCountOnInventory === true;
    const numericColumnCell = value => ({
      text: String(Number(value || 0)),
      alignment: "center",
      bold: true,
      color: COLORS.ink,
      fontSize: 9,
    });
    const lineItems = (invoice.InvoiceDetails || []).map((detail, index) => {
      const row = [];
      if (showLineNumber) row.push(numericColumnCell(index + 1));
      row.push(
        numericColumnCell(showBoxes ? detail.boxQty || detail.qty || 0 : detail.qty || 0),
        { text: clean(detail.name), color: COLORS.ink, fontSize: 9 },
      );
      if (showUnitPrice) row.push({ text: currency(Number(detail.price || 0) / unitsPerBox(detail)), alignment: "right" });
      row.push({ text: currency(Number(detail.price || 0)), alignment: "right" });
      row.push({ text: currency(Number(detail.price || 0) * Number(detail.qty || 0)), alignment: "right", bold: true });
      return row;
    });
    const totalRows = [["Subtotal", currency(invoice.total)], ["Total Paid", currency(invoice.payment)], ["Balance Due", currency(invoice.due)]];

    const content = [
      {
        columns: [
          { width: "*", stack: businessLines.map((line, index) => ({ text: line, color: COLORS.ink, bold: index === 0, fontSize: index === 0 ? 13 : 9, lineHeight: 1.2 })) },
          { width: 190, stack: [
            ...(logo ? [{ image: logo, fit: [72, 48], alignment: "right", margin: [0, 0, 0, 4] }] : []),
            { text: settings.invoiceLabel.toUpperCase(), alignment: "right", bold: true, fontSize: 27, color: COLORS.accent },
            { text: `#${clean(invoice.localId)}`, alignment: "right", bold: true, fontSize: 13, color: COLORS.ink, margin: [0, 3, 0, showInvoiceBarcode ? 8 : 0] },
            ...(barcode ? [{ image: barcode, width: 130, alignment: "right" }] : []),
          ] },
        ], margin: [0, 0, 0, 22],
      },
      {
        columns: [
          contactStack(billingLines, "BILL TO"), { width: 20, text: "" },
          contactStack(shippingLines, "SHIP TO"), { width: 30, text: "" },
          { width: 165, table: { widths: [65, "*"], body: [
            [{ text: "Date", color: COLORS.muted }, { text: settings.date, alignment: "right", noWrap: true }],
            [{ text: "Sales rep", color: COLORS.muted }, { text: clean(invoice.User && invoice.User.name), alignment: "right" }],
            [{ text: "Status", color: COLORS.muted }, { text: Number(invoice.due || 0) > 0 ? "Payment due" : "Paid", alignment: "right", bold: true, color: Number(invoice.due || 0) > 0 ? "#B45309" : "#15803D" }],
          ] }, layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 3, paddingBottom: () => 3 } },
        ], margin: [0, 0, 0, 24],
      },
      {
        table: { headerRows: 1, widths: [...(showLineNumber ? [22] : []), 36, "*", ...(showUnitPrice ? [55] : []), 60, 65], body: [[
          ...(showLineNumber ? [{ text: "#", color: "#FFFFFF", bold: true, alignment: "center" }] : []),
          { text: showBoxes ? "BOX" : "QTY", color: "#FFFFFF", bold: true, alignment: "center", noWrap: true },
          { text: "DESCRIPTION", color: "#FFFFFF", bold: true },
          ...(showUnitPrice ? [{ text: "U/PRICE", color: "#FFFFFF", bold: true, alignment: "right" }] : []),
          { text: "PRICE", color: "#FFFFFF", bold: true, alignment: "right" },
          { text: "TOTAL", color: "#FFFFFF", bold: true, alignment: "right" },
        ], ...lineItems] },
        layout: {
          fillColor: row => row === 0 ? COLORS.accent : row % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
          hLineColor: () => COLORS.border, vLineWidth: () => 0, hLineWidth: index => index === 0 ? 0 : 0.5,
          paddingTop: () => 8, paddingBottom: () => 8, paddingLeft: () => 7, paddingRight: () => 7,
        }, margin: [0, 0, 0, 18],
      },
      {
        columns: [
          { width: "*", stack: invoice.note ? [
            { text: "NOTE", bold: true, color: COLORS.muted, fontSize: 8, margin: [0, 0, 0, 5] },
            { text: clean(invoice.note), color: COLORS.ink, fontSize: 9, lineHeight: 1.2 },
          ] : [] },
          { width: 230, table: { widths: ["*", 90], body: totalRows.map((row, index) => {
            const dueRow = index === totalRows.length - 1;
            return [
              { text: row[0], bold: dueRow, fontSize: dueRow ? 12 : 9, color: dueRow ? "#FFFFFF" : COLORS.muted, margin: [4, dueRow ? 6 : 2, 0, dueRow ? 6 : 2] },
              { text: row[1], alignment: "right", bold: true, fontSize: dueRow ? 13 : 10, color: dueRow ? "#FFFFFF" : COLORS.ink, margin: [0, dueRow ? 6 : 2, 4, dueRow ? 6 : 2] },
            ];
          }) }, layout: { fillColor: row => row === totalRows.length - 1 ? COLORS.accent : COLORS.soft, hLineColor: () => "#FFFFFF", hLineWidth: () => 1, vLineWidth: () => 0 } },
        ], margin: [0, 0, 0, 24],
      },
    ];

    if (business.policy || paymentQr || exitQr) content.push({
      columns: [
        { width: "*", stack: business.policy ? [
          { text: "TERMS & POLICY", bold: true, color: COLORS.muted, fontSize: 7, margin: [0, 0, 0, 3] },
          { text: clean(business.policy), color: "#475569", fontSize: 6, lineHeight: 1.05 },
        ] : [] },
        ...(exitQr ? [{ width: 62, stack: [
          { text: "WAREHOUSE EXIT", bold: true, fontSize: 6, alignment: "center" },
          { image: exitQr, width: 48, alignment: "center", margin: [0, 2, 0, 0] },
        ], margin: [12, 0, 0, 0] }] : []),
        ...(paymentQr ? [{ width: 78, stack: [
          { text: "MAKE A PAYMENT", bold: true, fontSize: 6.5, alignment: "center", color: COLORS.accent },
          { image: paymentQr, width: 52, alignment: "center", margin: [0, 2, 0, 2] },
          { text: "Scan to pay securely", fontSize: 5.5, alignment: "center", color: COLORS.muted },
        ], margin: [14, 0, 0, 0] }] : []),
      ], margin: [0, 0, 0, 6],
    });

    content.push({ text: "Thank you for your business.", alignment: "center", bold: true, color: COLORS.accent, fontSize: 10, margin: [0, 10, 0, 0] });
    return content;
  },
  async invoiceHeader() { return null; },
  async invoiceFooter() {
    return (currentPage, pageCount) => ({
      columns: [{ text: "Powered by smplsale.com", color: COLORS.muted, fontSize: 7 }, { text: `${currentPage} of ${pageCount}`, alignment: "right", color: COLORS.muted, fontSize: 7 }],
      margin: [36, 0, 36, 14],
    });
  },
};
